"use client";
import React, { useState } from "react";
import { Carousel } from "react-responsive-carousel";
import {
  MagnifierZoom as OriginalMagnifierZoom,
  MagnifierContainer as OriginalMagnifierContainer,
} from "react-image-magnifiers";

import Multimedia from "@stadium/ui/src/components/multimedia/multimedia";
import { MagnifierPreview } from "@stadium/ui/src/components/image-magnifier";

import styles from "./main.module.scss";
import "../externalStyles.scss";
import isEmpty from "lodash/isEmpty";

const MagnifierZoom = (props) => (
  <OriginalMagnifierZoom {...OriginalMagnifierZoom.defaultProps} {...props} />
);
const MagnifierContainer = (props) => (
  <OriginalMagnifierContainer {...OriginalMagnifierContainer.defaultProps} {...props} />
);

const defaultRenderCustomOverlay = (active) => {
  if (active) return null;

  return (
    <div className={styles.hintContainer}>
      <svg viewBox="0 0 24 24" width="16" height="16" fill="white">
        <path d="M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z"></path>
      </svg>
    </div>
  );
};

const ImageCarouselWithMagnifier = ({
  selectedItem,
  onChange,
  thumbWidth,
  images = [],
  displayLoader = true,
  onLoad,
  fillAvailableSpace = false,
  renderCustomOverlay = defaultRenderCustomOverlay,
  loadingClassName = "",
  className = "",
  carouselContainerClass = "",
  customMagnifierZoomClass = "",
  thumbnailContainterClass = "",
  magnifierContentClass = "",
  isVerticalAxis = false,
  onClickItem,
  ...rest
}) => {
  const [isLoading, setIsLoading] = useState(displayLoader);

  const loadingClasses = isLoading ? `${"styles.imageLoading"} ${loadingClassName}` : "";
  const customClassName = `${className} ${loadingClasses}${styles.imageContainerMagnifier} image-container-magnifier`;

  const renderCustomThumbs = () => {
    return images.map(({ imageSrc, key, alt }) => <img key={key} src={imageSrc} alt={alt || ""} />);
  };

  const renderCarouselImage = ({ key, imageSrc, largeImageSrc, alt }) => (
    <div key={key}>
      <MagnifierPreview
        imageSrc={imageSrc || ""}
        largeImageSrc={largeImageSrc || imageSrc || ""}
        onImageLoad={() => {
          setIsLoading(false);
          onLoad?.();
        }}
        imageAlt={alt || ""}
        fillAvailableSpace={fillAvailableSpace}
        renderOverlay={renderCustomOverlay}
        className={customClassName}
        {...rest}
      />
    </div>
  );

  const renderCarousel = () => {
    if (isEmpty(images)) return null;

    if (images.length > 1) {
      return (
        <>
          {isVerticalAxis && (
            <div className={`${styles.verticalThumbs} ${thumbnailContainterClass}`}>
              {images.map((image, index) => (
                <div
                  className={`${styles.thumbImg} ${
                    selectedItem === index ? styles.activeThumb : ""
                  }`}
                  key={image.key}
                >
                  <Multimedia
                    src={image.imageSrc}
                    alt={image.alt || ""}
                    type="image"
                    height={thumbWidth}
                    width={thumbWidth}
                    isNextImage={false}
                    onClick={() => onChange(index)}
                  />
                </div>
              ))}
            </div>
          )}
          <Carousel
            {...Carousel.defaultProps}
            selectedItem={selectedItem}
            thumbWidth={thumbWidth}
            renderThumbs={isVerticalAxis ? undefined : renderCustomThumbs}
            showThumbs={!isVerticalAxis}
            onChange={onChange}
            onClickItem={onClickItem}
          >
            {images.map(renderCarouselImage)}
          </Carousel>
        </>
      );
    }

    return renderCarouselImage(images[0]);
  };

  return (
    <MagnifierContainer>
      <div className={`${styles.magnifierContent} ${magnifierContentClass}`}>
        <div className={carouselContainerClass} data-type="image-carousel">
          {renderCarousel()}
        </div>
        <div className={`${styles.magnifierZoom} ${customMagnifierZoomClass}`}>
          <MagnifierZoom
            key={images[selectedItem]?.key}
            className={styles.imageContainerMagnifier}
            imageSrc={images[selectedItem]?.imageSrc || ""}
            largeImageSrc={
              images[selectedItem]?.largeImageSrc || images[selectedItem]?.imageSrc || ""
            }
            imageAlt={images[selectedItem]?.alt || ""}
          />
        </div>
      </div>
    </MagnifierContainer>
  );
};

export default ImageCarouselWithMagnifier;
